import { formatOptionalNumber, isFiniteNumber, normalizeString } from '../../shared/checklistUtils';
import type { SetupRuleInput } from '../setup/types';

import { buildCampaignRow, formatStringValue } from './campaignRow';
import type { CampaignChecklistRow } from './types';

export type PolicyBibleCampaignRule = {
  chain: string;
  dateChangeAllowedDays?: number;
  geo: string;
  numberOfDateChanges?: string;
};

export const POLICY_BIBLE_CAMPAIGN_RULES: Array<PolicyBibleCampaignRule> = [
  { chain: 'Accor', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Accor', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Ascott', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ascott', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ayana', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Clermont', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Grecotel', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Highgate', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Hilton', geo: 'Intl' },
  { chain: 'Hilton', geo: 'Australia' },
  { chain: 'Hyatt', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Hyatt', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'IHG', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'IHG', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Kerzner', geo: 'Australia' },
  { chain: 'Kerzner', geo: 'Intl' },
  { chain: 'Kimpton', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Lalit', geo: 'Intl' },
  { chain: 'Langham', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Langham', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Marriott', geo: 'Intl/Aus', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Melia', geo: 'Intl' },
  { chain: 'Minor', geo: 'Australia' },
  { chain: 'Minor', geo: 'Intl' },
  { chain: 'Mulia', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Nomade', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: '2' },
  { chain: 'Oberoi', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Oberoi', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'RACV', geo: 'Australia' },
  { chain: 'Shangri La', geo: 'Intl' },
  { chain: 'Shangri La', geo: 'Australia' },
  { chain: 'Standard', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'International', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'USA', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard (GOLF wholesale)', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  {
    chain: 'Standard (GOLF WHOLESALE)',
    geo: 'Australia',
    dateChangeAllowedDays: 21,
    numberOfDateChanges: 'Unlimited',
  },
  { chain: 'TFE', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'TFE', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'The Star', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: '1' },
  { chain: 'Two Roads', geo: 'Intl' },
  { chain: 'Vinpearl', geo: 'Intl' },
];

// Normalized LE_Hotel_Chains__c picklist value -> normalized Policy Bible chain key, for values that don't match exactly.
export const POLICY_BIBLE_CHAIN_ALIASES: Record<string, string> = {
  'standard template': 'standard',
  marriot: 'marriott',
  'clermont hotels': 'clermont',
  'golf wholesale': 'standard (golf wholesale)',
  glh: 'clermont',
};

function resolvePolicyBibleChainAlias(normalizedValue: string) {
  return POLICY_BIBLE_CHAIN_ALIASES[normalizedValue] ?? normalizedValue;
}

export function findPolicyBibleCampaignRule(setupInput?: SetupRuleInput) {
  const normalizedChainNames = getPolicyBibleChainCandidates(setupInput);
  const normalizedGeos = getPolicyBibleGeoCandidates(setupInput);

  if (normalizedChainNames.length === 0 || normalizedGeos.length === 0) {
    return undefined;
  }

  return POLICY_BIBLE_CAMPAIGN_RULES.find(
    (rule) =>
      normalizedChainNames.includes(normalizePolicyBibleKey(rule.chain) ?? '') &&
      normalizedGeos.some((normalizedGeo) => policyBibleGeoMatches(rule.geo, normalizedGeo)),
  );
}

export function buildPolicyBibleNumberRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: number;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.dateChangeAllowedDays;
  const approved = isFiniteNumber(loadedValue) && isFiniteNumber(expectedValue) && loadedValue === expectedValue;

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatOptionalNumber(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    policyBibleRule,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

export function buildPolicyBibleTextRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.numberOfDateChanges;
  const approved =
    !!normalizeString(loadedValue) &&
    !!normalizeString(expectedValue) &&
    normalizeComparable(loadedValue) === normalizeComparable(expectedValue);

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatStringValue(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    policyBibleRule,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

function policyBibleGeoMatches(ruleGeo: string, loadedGeo: string) {
  const normalizedRuleGeo = normalizePolicyBibleGeo(ruleGeo);

  if (!normalizedRuleGeo) {
    return false;
  }

  if (normalizedRuleGeo === loadedGeo) {
    return true;
  }

  return normalizedRuleGeo
    .split('/')
    .map((geoPart) => normalizePolicyBibleGeo(geoPart))
    .includes(loadedGeo);
}

function normalizePolicyBibleKey(value?: string | null) {
  return normalizeString(value)?.toLowerCase().replace(/&/g, 'and').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

// Normalized real geo value -> normalized Policy Bible geo bucket.
export const POLICY_BIBLE_GEO_ALIASES: Record<string, string> = {
  au: 'australia',
  aus: 'australia',
  australia: 'australia',
  intl: 'intl',
  international: 'intl',
  'intl/aus': 'intl/australia',
  'intl aus': 'intl/australia',
  'international/australia': 'intl/australia',
  'international australia': 'intl/australia',
  us: 'usa',
  usa: 'usa',
  'united states': 'usa',
  'united states of america': 'usa',
  nz: 'intl',
  'new zealand': 'intl',
};

function normalizePolicyBibleGeo(value?: string | null) {
  const normalizedValue = normalizePolicyBibleKey(value);

  if (!normalizedValue) {
    return normalizedValue;
  }

  return POLICY_BIBLE_GEO_ALIASES[normalizedValue] ?? normalizedValue;
}

function getPolicyBibleChainCandidates(setupInput?: SetupRuleInput) {
  return uniqueNormalizedValues([setupInput?.opportunity?.chain], resolvePolicyBibleChainKey);
}

function resolvePolicyBibleChainKey(value?: string | null) {
  const normalizedValue = normalizePolicyBibleKey(value);

  return normalizedValue ? resolvePolicyBibleChainAlias(normalizedValue) : normalizedValue;
}

function getPolicyBibleGeoCandidates(setupInput?: SetupRuleInput) {
  return uniqueNormalizedValues([setupInput?.opportunity?.geo, setupInput?.property?.country], normalizePolicyBibleGeo);
}

function uniqueNormalizedValues(
  values: Array<string | null | undefined>,
  normalize: (value?: string | null) => string | undefined = normalizePolicyBibleKey,
) {
  return Array.from(new Set(values.map((value) => normalize(value)).filter((value): value is string => !!value)));
}

function getPolicyBibleEvidence({
  approved,
  expectedValue,
  loadedValue,
  policyBibleRule,
}: {
  approved: boolean;
  expectedValue?: number | string;
  loadedValue?: number | string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}) {
  if (!policyBibleRule) {
    return 'Policy Bible expected value is not available for the loaded chain and geo.';
  }

  if (expectedValue == null || expectedValue === '') {
    return 'Policy Bible expected value is blank or ambiguous for the loaded chain and geo.';
  }

  if (loadedValue == null || loadedValue === '') {
    return 'Loaded value is missing.';
  }

  return approved ? 'Loaded value matches the Policy Bible.' : 'Loaded value does not match the Policy Bible.';
}

function normalizeComparable(value?: string | null) {
  return value?.trim().toLowerCase() || '';
}
